import * as React from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { DispensaryContext } from '../dispensary/DispensaryProvider';
import { setDispensaryProducts, addToCart } from '../actions/cartActions';
import { loadDispensaryProducts } from '../products/product-utils';
import { trackEvent } from '../analytics/tracking';
import { navigateTop } from '../routing/router-utils';
import BodyWrapper from '../layout/BodyWrapper';
import WideScreenOnly from '../mediaQuery/WideScreenOnly';
import Cart from '../checkout/Cart';
import Spinner from '../common/Spinner';
import { LayoutTypes } from '../layout/BodyWrapper';
import DispensaryHeader from '../dispensary/DispensaryHeader';
import ProductFilterNav from '../dispensary/ProductFilterNav';
import AddedToCartCheck from '../products/AddedToCartCheck';
import Helmet from 'react-helmet';
import ProductDetailsCard from './ProductDetailsCard';

import PropTypes from 'prop-types';

import styles from '../dispensary/Dispensary.module.css';

/**
 * This page/view is mostly for SEO, currently a product card click
 * will display a modal not a full page view
 * 
 * Note: viewing this page would likely flush cart items from a different dispensary
 */
const ProductDetailPage = ({dispensaryId, itemId}) => {
  const productsById = useSelector(state => state.productsById);

  const { dispensary, setDispensaryId } = React.useContext(DispensaryContext);
  const dispatch = useDispatch();

  const [product, setProduct] = React.useState();
  const [productCategory, setProductCategory] = React.useState();

  const [showAddedCheck, setShowAddedCheck] = React.useState();
 
  // for scrolling 
  const filterRef = React.useRef();

  // Get the product based on the url
  const setProductItem = React.useCallback((dispensaryId, itemId, controller) => {
    const productCallback = (products) => {
      const byId = products.data.reduce((map, product) => map.set(product.id, product), new Map());
      
      const productItem = byId.get(itemId);
      setProduct(productItem);
      const productCategory = productItem.display_info.category
        ? productItem.display_info.category.display_name
        : '';
      setProductCategory(productCategory);
      filterRef.current && filterRef.current.scrollIntoView();
      // update redux
      dispatch(setDispensaryProducts(dispensaryId, products));
    };
    
    try {
      if (!product) {
        if (productsById && productsById[dispensaryId]) {
          const dispensaryProducts = productsById[dispensaryId];  
          const productItem = dispensaryProducts.get(itemId);
          console.log('using cached product');
          if (productItem) {
            setProduct(productItem);
            setProductCategory(productItem.display_info.category_name);
          }
        } else {
          console.log('fetching product data');
          loadDispensaryProducts(dispensaryId, productCallback, controller);
        }
      }
    } catch(error) {
      trackEvent('error_fetching_products',`${dispensaryId}:${error}`);
    }
  }, [dispatch, product, productsById]);
  
  const abortController = React.useRef(new AbortController());

  // Load dispensary + product
  React.useEffect(() => {
    const controller = abortController.current;
    
    if (dispensaryId && !dispensary) {
      setDispensaryId(dispensaryId);
    }
    if (!product) {
      setProductItem(dispensaryId, itemId, controller);
    } 
    // TODO: Use Provider -- will not complete if controller aborted
    // return () => { controller && controller.abort() }; 
  }, [dispensary, dispensaryId, setDispensaryId, itemId, product, setProductItem, abortController]); 

  const handleClick = (quantity) => {
    setShowAddedCheck(true);
    dispatch(addToCart(product.id, null, quantity));
  }

  const navigateToDispensaryPage = (category) => {
    // the anchor won't actually work ine React here...
    navigateTop(`/dispensary/${dispensary.id}/${category}#category_filter`,{disableScroll:true});
  } 
  
  return ( 
    <BodyWrapper pageLayout={LayoutTypes.FullBleed}>
      { dispensary && product && productCategory
        ? <>
            <Helmet>
              <title>Zip Run - {product.display_info.name}</title>
              <meta name="description" content={product.display_info.description} />
            </Helmet>
            <div className={styles.dispensaryGrid}>
              <div className={styles.dispensaryCol}> 
                <div className={styles.dispensaryColLayout}>
                  <DispensaryHeader { ...dispensary } />
                  <div ref={filterRef}></div> 
                  <ProductFilterNav currentCategory={productCategory} handleClick={navigateToDispensaryPage} />  
               
                  <div className={styles.productDetailPageWrap}>
                    <ProductDetailsCard  
                      Id={product.id}
                      {...product.display_info}
                      productGrams={product.grams}
                      leaf_logix_pricing_tier={product.leaf_logix_pricing_tier} 
                      imageInfo={{
                        productImage: product.display_info.image_url,
                        productCategory: product.display_info.category?.display_name
                      }}
                      isModal={false}
                      handleClick={handleClick} 
                    />  
                  </div> 
                </div>
              </div>
              <WideScreenOnly>
                <div className={styles.cartCol}> 
                  <Cart dispensaryName={dispensary?.name} closeMenuFn={()=>{}} />
                </div>
              </WideScreenOnly>
            </div>
          </>
        : <Spinner />
      }
      { showAddedCheck && 
        <AddedToCartCheck closeFn={() => { setShowAddedCheck(false)} } />
      }
    </BodyWrapper>
  )
}  

ProductDetailPage.propTypes = {
  dispensaryId: PropTypes.string,
  itemId: PropTypes.string
};

export default ProductDetailPage;